import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import { BookOpen, CheckCircle, Target, TrendingUp, BarChart, PieChart, LineChart, Shield, CreditCard, DollarSign, Gift, Radio, Heart } from 'lucide-react';

const guides = [
  {
    id: 'choisir-site-paris',
    title: 'Comment Choisir un Site de Paris en Ligne',
    description: 'Les critères essentiels pour sélectionner un opérateur fiable et adapté à vos besoins',
    icon: Shield,
    color: 'from-blue-600 to-blue-800',
  },
  {
    id: 'comprendre-cotes',
    title: 'Comprendre les Cotes et les Probabilités',
    description: 'Décryptage des cotes françaises, décimales et anglaises pour optimiser vos paris',
    icon: TrendingUp,
    color: 'from-green-600 to-green-800',
  },
  {
    id: 'types-paris',
    title: 'Types de Paris : Simple, Combiné, Système',
    description: 'Maîtrisez tous les formats de paris pour diversifier vos stratégies',
    icon: Target,
    color: 'from-purple-600 to-purple-800',
  },
  {
    id: 'gerer-bankroll',
    title: 'Gérer son Bankroll Efficacement',
    description: 'Techniques professionnelles pour préserver et faire croître votre capital',
    icon: DollarSign,
    color: 'from-yellow-600 to-yellow-800',
  },
  {
    id: 'methodes-paiement',
    title: 'Les Méthodes de Paiement et Retraits',
    description: 'Tout savoir sur les dépôts sécurisés et les retraits rapides',
    icon: CreditCard,
    color: 'from-pink-600 to-pink-800',
  },
  {
    id: 'securite-licences',
    title: 'Sécurité et Licences ANJ',
    description: 'Comprendre les garanties offertes par les sites agréés en France',
    icon: Shield,
    color: 'from-red-600 to-red-800',
  },
  {
    id: 'bonus-promotions',
    title: 'Les Bonus et Promotions',
    description: 'Comment identifier et maximiser les bonus réellement avantageux',
    icon: Gift,
    color: 'from-indigo-600 to-indigo-800',
  },
  {
    id: 'paris-direct',
    title: 'Parier en Direct (Live Betting)',
    description: 'Stratégies pour profiter des opportunités en temps réel',
    icon: Radio,
    color: 'from-orange-600 to-orange-800',
  },
  {
    id: 'jeu-responsable',
    title: 'Jeu Responsable et Limites',
    description: 'Protégez-vous avec les outils de jeu responsable disponibles',
    icon: Heart,
    color: 'from-teal-600 to-teal-800',
  },
];

const Guides = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Guides Complets pour Parier en Ligne en France",
    "description": "Toutes les connaissances nécessaires pour devenir un parieur avisé et maximiser vos chances de succès",
    "url": "https://galo-bet.com/guides",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": guides.map((guide, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": guide.title,
        "url": `https://galo-bet.com/guides/${guide.id}`
      }))
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <SEO 
        title="Guides Complets"
        description="Toutes les connaissances nécessaires pour devenir un parieur avisé et maximiser vos chances de succès. Guides complets pour parier en ligne en France."
        jsonLd={jsonLd}
      />
      <Header />
      <main className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* Hero Section */}
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                <span className="bg-gradient-to-r from-pink-400 to-pink-600 bg-clip-text text-transparent">
                  Guides Complets
                </span>
                <br />
                <span className="text-white">Pour Parier en Ligne en France</span>
              </h1>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                Toutes les connaissances nécessaires pour devenir un parieur avisé et maximiser vos chances de succès
              </p>
            </div>

            {/* How We Write Guides Section */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 md:p-12 border border-pink-500/20 mb-16">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-pink-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BookOpen className="text-white" size={24} />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-white mb-6">
                    Comment Nous Rédigeons Nos Guides
                  </h2>
                  <div className="space-y-4 text-gray-300 leading-relaxed">
                    <p>
                      Chaque guide que nous publions est le fruit d'un travail méticuleux qui combine expérience pratique, analyse approfondie et vérification constante. Notre équipe teste personnellement chaque site de paris, chaque fonctionnalité, et chaque processus pour vous livrer des informations précises et à jour.
                    </p>
                    <p>
                      Nous ne nous contentons pas de compiler des données théoriques. Nos guides sont bâtis sur des expériences réelles de parieurs, des retours de la communauté, et des tests pratiques effectués dans les conditions réelles du marché français. Chaque aspect technique est expliqué de manière claire, sans jargon inutile, pour que vous puissiez passer à l'action immédiatement.
                    </p>
                    <p>
                      La transparence est notre principe fondamental. Nous vous expliquons non seulement ce qu'il faut faire, mais aussi pourquoi c'est important, quels sont les pièges à éviter, et comment adapter nos conseils à votre situation personnelle. Nous abordons aussi bien les stratégies avancées que les bases essentielles, car nous savons qu'un parieur bien informé est un parieur qui prend de meilleures décisions.
                    </p>
                    <p>
                      Nos guides sont régulièrement mis à jour pour refléter les évolutions du marché, les nouvelles réglementations de l'ANJ, et les changements dans les pratiques des opérateurs. Nous suivons de près les tendances, les nouvelles fonctionnalités, et les meilleures pratiques qui émergent dans le secteur des paris en ligne français.
                    </p>
                    <p>
                      Enfin, nous privilégions toujours l'approche pédagogique. Notre objectif n'est pas seulement de vous donner des informations, mais de vous former à devenir un parieur autonome et stratégique. Chaque guide est structuré pour progresser du général au spécifique, avec des exemples concrets, des cas pratiques, et des conseils actionnables que vous pouvez appliquer dès maintenant.
                    </p>
                  </div>
                </div>
              </div>
            </div>

          

            {/* Guides Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {guides.map((guide) => {
                const Icon = guide.icon;
                return (
                  <Link
                    key={guide.id}
                    to={`/guides/${guide.id}`}
                    data-discover="true"
                    className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700 hover:border-pink-600 transition-all transform hover:-translate-y-2 flex flex-col"
                  >
                    <div className={`w-14 h-14 bg-gradient-to-br ${guide.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon className="text-white" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-pink-400 transition-colors">
                      {guide.title}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4 flex-1">
                      {guide.description}
                    </p>
                    <div className="flex items-center text-pink-400 font-semibold text-sm">
                      <span>Lire le guide</span>
                      <CheckCircle className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Guides;
